import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Typography,
  ToggleButton,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Autocomplete,
} from '@mui/material';
import {
  Close as CloseIcon,
  CropFree as DrawIcon,
  Visibility as ViewIcon,
  DeleteOutline as DeleteOutlineIcon,
  Grain as XRayIcon,
  RestoreFromTrash as RestoreIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { useTheme } from '@mui/material/styles';
import { toast } from 'react-toastify';
import api from '../shared/services/api';
import { useAuthStore } from '../store/authStore';
import { colors } from '../theme';
import { useTranslation } from '../store/languageStore';

// Configure PDF.js worker
// Use jsDelivr CDN URL for PDF.js worker (supports CORS, using .mjs module)
console.log('Configuring PDF.js worker...');
pdfjs.GlobalWorkerOptions.workerSrc = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@5.4.296/build/pdf.worker.min.mjs';
console.log('PDF.js worker configured:', pdfjs.GlobalWorkerOptions.workerSrc);

interface TeachAIModalProps {
  open: boolean;
  onClose: () => void;
  pdfUrl: string;
  fileName: string;
  fileId: string;
  onFieldMapped: (fieldPath: string, value: string) => void;
  /** Called when the user successfully deletes a rule, so the parent can clear the live form value */
  onFieldDeleted?: (fieldPath: string) => void;
  /** Called when the user restores a previously-suppressed field, so the parent can re-populate the live form value */
  onFieldRestored?: (fieldPath: string, value: string) => void;
  /**
   * @deprecated No longer used. The overlay now sources located values from the
   * Ghost Layer payload (/teach-ai/xray), not from this prop. Kept optional so
   * existing callers still type-check; safe to drop when callers stop passing it.
   */
  extractedData?: Record<string, any>;
  /**
   * Optional list of all available files the user could teach on.
   * When provided AND length > 1, a dropdown is rendered so the user
   * can switch between files without closing the modal.
   * Files without a fileId should be filtered out by the parent.
   */
  availableFiles?: Array<{ fileId: string; name: string }>;
  /**
   * Called when the user picks a different file from the in-modal dropdown.
   * Parent should update its selectedFileForAnnotation state so the
   * pdfUrl/fileId/fileName/extractedData props passed back to this modal
   * reflect the new selection.
   */
  onFileChange?: (fileId: string) => void;
}

interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
  pageNumber: number;
}

interface SavedBoundingBox extends BoundingBox {
  fieldPath: string;
  fieldLabel: string;
  extractedText: string;
  /** Provenance: 'mapped' = user-taught rule box; 'extracted' = AI/ghost box. */
  source?: 'mapped' | 'extracted';
}

interface MappedField {
  field_path: string;
  field_label: string;
  bounding_box: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  extracted_text: string;
  confidence: number;
  page_number: number;
}

// ─────────────────────────── Ghost Layer X-Ray ───────────────────────────
type GroundingState = 'cited' | 'fallback' | 'derived' | 'unresolved';

/** A located field enriched with how it was grounded (from /teach-ai/xray). */
interface XRayBox extends SavedBoundingBox {
  groundingState: GroundingState;
  sourceBox?: string | string[] | null;
}

/** One PP-OCR point-cloud word, in 72pt PDF-point space, for snap-to-box. */
interface PointCloudWord {
  id: string;
  text: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

// Overlay colour per grounding state (GHOST_LAYER_ARCHITECTURE.md §5.4):
//   cited      → theme accent — LLM cited the exact box id (the shown state)
//   fallback   → amber  — located by fuzzy value-match (worth a glance)
//   derived    → slate  — computed/inferred, no box (legend only)
//   unresolved → red    — couldn't be located, no box (legend only)
const GROUNDING_COLORS: Record<GroundingState, string> = {
  cited: colors.primary, // theme accent — see theme.ts (brand orange)
  fallback: '#f59e0b',
  derived: '#64748b',
  unresolved: '#ef4444',
};

const GROUNDING_LABELS: Record<GroundingState, string> = {
  cited: 'Cited',
  fallback: 'Matched',
  derived: 'Derived',
  unresolved: 'Not found',
};

const TeachAIModal: React.FC<TeachAIModalProps> = ({
  open,
  onClose,
  pdfUrl,
  fileName,
  fileId,
  onFieldMapped,
  onFieldDeleted,
  onFieldRestored,
  availableFiles,
  onFileChange,
}) => {
  const [drawMode, setDrawMode] = useState(false);
  const [showMappedFields, setShowMappedFields] = useState<boolean>(true);
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [boundingBox, setBoundingBox] = useState<BoundingBox | null>(null);
  const [savedBoxes, setSavedBoxes] = useState<SavedBoundingBox[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState<{ x: number; y: number } | null>(null);
  const [extractedText, setExtractedText] = useState<string>('');
  const [isExtracting, setIsExtracting] = useState(false);
  const [isLoadingMappedFields, setIsLoadingMappedFields] = useState(false);
  const [showFieldMapper, setShowFieldMapper] = useState(false);
  const [selectedField, setSelectedField] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [isResettingRules, setIsResettingRules] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  // Box the user asked to delete (mapped) / suppress (extracted), pending
  // confirmation. Null = no confirm dialog open.
  const [confirmDelete, setConfirmDelete] = useState<SavedBoundingBox | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  // Vendor-wide suppressed fields, for the per-field restore list.
  const [suppressedFields, setSuppressedFields] = useState<
    Array<{ fieldPath: string; suppressedValue: string | null }>
  >([]);
  const [showRestoreList, setShowRestoreList] = useState(false);
  // field_path currently being restored (per-row spinner); null = none.
  const [restoringPath, setRestoringPath] = useState<string | null>(null);
  const [isSavingRule, setIsSavingRule] = useState(false);
  const [pageDimensions, setPageDimensions] = useState<{ width: number; height: number } | null>(null);
  const [pagePosition, setPagePosition] = useState<{ left: number; top: number } | null>(null);
  const [pageInfo, setPageInfo] = useState<{
    pdfWidth: number;
    pdfHeight: number;
    renderWidth: number;
    renderHeight: number;
    scale: number;
  } | null>(null);
  const [selectedBox, setSelectedBox] = useState<SavedBoundingBox | null>(null);
  const [isHoveringBox, setIsHoveringBox] = useState(false);
  // ── X-Ray overlay (Ghost Layer enriched payload: state-coloured boxes) ──
  const [showXRay, setShowXRay] = useState<boolean>(true);
  const [xrayAvailable, setXrayAvailable] = useState<boolean>(false);
  const [xrayBoxes, setXrayBoxes] = useState<XRayBox[]>([]);
  const [pointCloud, setPointCloud] = useState<PointCloudWord[]>([]);
  // fieldPath currently hovered (in the canvas OR the field list) — drives the
  // bi-directional highlight between the two.
  const [hoveredFieldPath, setHoveredFieldPath] = useState<string | null>(null);
  // box_ids the last drawn region snapped to (sent with save-rule).
  const [snappedBoxIds, setSnappedBoxIds] = useState<string[]>([]);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const pageRef = useRef<HTMLDivElement>(null);
  // Latest X-Ray request key ("<fileId>:<page>"); a resolving load whose key no
  // longer matches has been superseded by a newer file/page and is discarded.
  const xrayReqRef = useRef<string>('');
  // Latest suppressed-fields request key (fileId); a resolving load whose key no
  // longer matches has been superseded by a newer file and is discarded.
  const suppressedReqRef = useRef<string>('');

  // Theme & translations
  const isDarkMode = false;
  const theme = useTheme();
  const { tString } = useTranslation();

  // Fetch PDF as blob when modal opens OR when the active file changes via
  // the in-modal picker. pdfUrl changes whenever the parent swaps the
  // selected file, so this effect handles both cases uniformly and resets
  // every piece of file-scoped state so we never carry doc-1 boxes /
  // selections / page position into doc-2.
  useEffect(() => {
    if (open && pdfUrl) {
      setPdfBlob(null);
      setError('');
      setSavedBoxes([]);
      setXrayBoxes([]);
      setPointCloud([]);
      setXrayAvailable(false);
      setHoveredFieldPath(null);
      setSnappedBoxIds([]);
      setBoundingBox(null);
      setSelectedBox(null);
      setExtractedText('');
      setShowFieldMapper(false);
      setSelectedField('');
      setDrawMode(false);
      setCurrentPage(1);  // start at page 1 on every file switch
      setNumPages(0);
      setPageDimensions(null);
      setPagePosition(null);
      setPageInfo(null);
      fetchPdfAsBlob();
    }
  }, [open, pdfUrl]);

  // Load mapped fields when page changes or modal opens
  useEffect(() => {
    if (open && fileId && currentPage && pageDimensions) {
      loadMappedFields();
    }
  }, [open, fileId, currentPage, pageDimensions]);

  // Load the Ghost Layer X-Ray payload (state-coloured field boxes + point
  // cloud) on open / page change. Cheap cache read; the point cloud is needed
  // for snap-to-box even when the X-Ray overlay toggle is off.
  useEffect(() => {
    if (open && fileId && currentPage && pageDimensions) {
      loadXRay();
    }
  }, [open, fileId, currentPage, pageDimensions]);

  // Load the vendor's suppressed fields (for the restore list). Vendor-wide, so
  // it does NOT depend on the current page — refetched on open / file change.
  useEffect(() => {
    if (open && fileId) {
      loadSuppressedFields();
    }
  }, [open, fileId]);

  // Fetch the fields the user has previously deleted (suppressed) for this
  // vendor template, so they can be restored one-by-one from the restore list.
  const loadSuppressedFields = async () => {
    if (!fileId) return;
    // Guard against out-of-order responses on a fast file switch (mirrors
    // loadXRay): drop a resolved load whose file is no longer the active one.
    const reqKey = fileId;
    suppressedReqRef.current = reqKey;
    try {
      const response = await api.post('/api/v1/teach-ai/suppressed-fields', {
        file_id: fileId,
      });
      if (suppressedReqRef.current !== reqKey) return;
      const fields = (response.data?.fields || []).map((f: any) => ({
        fieldPath: f.field_path,
        suppressedValue: f.suppressed_value ?? null,
      }));
      setSuppressedFields(fields);
    } catch (err) {
      if (suppressedReqRef.current !== reqKey) return;
      // Non-fatal: the restore button just stays disabled at 0.
      console.error('Error loading suppressed fields:', err);
      setSuppressedFields([]);
    }
  };

  const loadMappedFields = async () => {

    setIsLoadingMappedFields(true);
    try {
      console.log('🎨 Loading mapped fields for page', currentPage);

      const response = await api.post('/api/v1/teach-ai/highlight-mapped-fields', {
        file_id: fileId,
        page_number: currentPage
      });

      console.log('✅ Mapped fields loaded:', response.data);

      // Convert mapped fields to SavedBoundingBox format
      const mappedBoxes: SavedBoundingBox[] = response.data.mapped_fields.map((field: MappedField) => ({
        x: field.bounding_box.x,
        y: field.bounding_box.y,
        width: field.bounding_box.width,
        height: field.bounding_box.height,
        pageNumber: field.page_number,
        fieldPath: field.field_path,
        fieldLabel: field.field_label,
        extractedText: field.extracted_text,
        source: 'mapped' as const,
      }));

      setSavedBoxes(mappedBoxes);
      console.log('🔄 setSavedBoxes called with:', mappedBoxes);
      console.log('🔄 Current state - pageInfo:', pageInfo, 'canvasRef:', canvasRef.current);

      if (mappedBoxes.length > 0) {
        console.log(`📦 Loaded ${mappedBoxes.length} mapped fields for page ${currentPage}`);
      } else {
        console.log(`ℹ️ No previously mapped fields found for this page`);
      }

    } catch (err: any) {
      console.error('❌ Error loading mapped fields:', err);
      // Don't show error to user - this is optional enhancement
      // If it fails, they can still use teach AI normally
      // Just log for debugging
      if (err.response?.status === 400) {
        console.log('ℹ️ Document needs to be processed first to enable field highlighting');
      }
    } finally {
      setIsLoadingMappedFields(false);
    }
  };

  // Fetch the enriched Ghost Layer payload for this page: fields already
  // located + coloured by grounding state, plus the point cloud for snapping.
  // Reads a cache on the backend (no OCR); degrades to empty when the file was
  // processed before eager grounding existed (available=false).
  const loadXRay = async () => {
    if (!fileId || !currentPage) return;
    // Guard against out-of-order responses: if the user switches file/page
    // before this resolves, a newer load will have overwritten the ref key and
    // we drop this (now stale) response instead of leaking it into the new doc.
    const reqKey = `${fileId}:${currentPage}`;
    xrayReqRef.current = reqKey;
    try {
      const response = await api.post('/api/v1/teach-ai/xray', {
        file_id: fileId,
        page_number: currentPage,
      });
      if (xrayReqRef.current !== reqKey) return;
      const data = response.data || {};
      const available = !!data.available;
      setXrayAvailable(available);

      // Show ONLY values genuinely read off the invoice — i.e. CITED (the model
      // pinned the value to a specific box on the page). MATCHED (fuzzy guesses)
      // and derived/normalized values (e.g. country code "IN" vs "REPUBLIC OF
      // INDIA") are excluded so the overlay reflects the document, nothing else.
      const boxes: XRayBox[] = (data.fields || [])
        .filter((f: any) => f.grounding_state === 'cited')
        .map((f: any) => ({
          x: f.bounding_box.x,
          y: f.bounding_box.y,
          width: f.bounding_box.width,
          height: f.bounding_box.height,
          pageNumber: f.page_number,
          fieldPath: f.field_path,
          fieldLabel: f.field_label,
          extractedText: f.extracted_text || '',
          groundingState: (f.grounding_state || 'cited') as GroundingState,
          sourceBox: f.source_box ?? null,
          source: 'extracted' as const,
        }));
      setXrayBoxes(boxes);

      const words: PointCloudWord[] = (data.point_cloud || []).map((w: any) => ({
        id: w.id,
        text: w.text,
        x: w.bounding_box.x,
        y: w.bounding_box.y,
        width: w.bounding_box.width,
        height: w.bounding_box.height,
      }));
      setPointCloud(words);
      console.log(
        `🩻 X-Ray: available=${available}, ${boxes.length} fields, ${words.length} words on page ${currentPage}`
      );
    } catch (err) {
      if (xrayReqRef.current !== reqKey) return;
      console.error('Error loading X-Ray payload:', err);
      setXrayAvailable(false);
      setXrayBoxes([]);
      setPointCloud([]);
    }
  };

  const fetchPdfAsBlob = async () => {
    setIsLoadingPdf(true);
    setError('');

    try {
      console.log('📄 Fetching PDF from URL:', pdfUrl);

      try {
        // First try using api instance
        const response = await api.get(pdfUrl, {
          responseType: 'blob',
        });

        console.log('✅ PDF fetch via api instance successful:', response.status);
        console.log('📄 PDF blob created via api, size:', response.data.size, 'type:', response.data.type);
        setPdfBlob(response.data);
      } catch (apiError: any) {
        console.log('🔄 API instance failed for PDF, trying fetch fallback...', apiError);

        // Fallback to fetch
        const { token } = useAuthStore.getState();

        const fetchResponse = await fetch(pdfUrl, {
          headers: {
            'Authorization': `Bearer ${token?.access_token || ''}`,
          },
        });

        console.log('📄 PDF fetch response status:', fetchResponse.status);

        if (!fetchResponse.ok) {
          throw new Error(`Failed to fetch PDF: ${fetchResponse.status} ${fetchResponse.statusText}`);
        }

        const blob = await fetchResponse.blob();
        console.log('📄 PDF blob created via fetch, size:', blob.size, 'type:', blob.type);
        setPdfBlob(blob);
      }
    } catch (err) {
      console.error('❌ Error fetching PDF:', err);
      setError(`Failed to load PDF: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoadingPdf(false);
    }
  };

  // Available fields for mapping (from init_customs_module.py get_field_mappings() and AtlasValidationPreview.tsx)
  const availableFields = [
    // ===== Header (Kopf) Fields =====
    { value: 'kopf.lrn', label: 'LRN (Local Reference Number)' },
    { value: 'kopf.artderAnmeldung', label: 'Declaration Type' },
    { value: 'kopf.artderAusfuhranmeldung', label: 'Export Declaration Type' },
    { value: 'kopf.beteiligtenKonstellation', label: 'Participant Constellation' },
    { value: 'kopf.zeitpunktderAnmeldung', label: 'Declaration Date' },
    { value: 'kopf.sicherheit', label: 'Security Indicator' },
    { value: 'kopf.gestellungszollstelle', label: 'Customs Office of Presentation' },
    { value: 'kopf.ausfuhrzollstelleDienststellennummer', label: 'Export Customs Office Number' },
    { value: 'kopf.inRechnunggestellterGesamtbetrag', label: 'Total Amount' },
    { value: 'kopf.rechnungswahrung', label: 'Currency' },
    { value: 'kopf.datum', label: 'Date' },
    { value: 'kopf.containerIndikator', label: 'Container Indicator' },
    { value: 'kopf.anzahlPositionen', label: 'Number of Positions' },
    { value: 'kopf.bezugsnummer', label: 'Reference Number' },
    { value: 'kopf.eoriNiederlassungsnummer', label: 'EORI Branch Number' },
    { value: 'kopf.vorgeseheneAusgangszollstelleDienststellennummer', label: 'Intended Exit Customs Office' },
    { value: 'kopf.ladeort.code', label: 'Loading Place Code' },
    { value: 'kopf.beforderungsmittelderGrenzeArt', label: 'Border Transport Type' },
    { value: 'kopf.beforderungsmittelderGrenzeStaatszugehorigkeit', label: 'Border Transport Nationality' },
    { value: 'kopf.ausfuhrland', label: 'Export Country' },
    { value: 'kopf.bestimmungsLand', label: 'Destination Country' },
    { value: 'kopf.lieferbedingungIncotermCode', label: 'Incoterm Code' },
    { value: 'kopf.lieferbedingungOrt', label: 'Delivery Place' },
    { value: 'kopf.lieferbedingungLand', label: 'Delivery Country' },
    { value: 'kopf.inlandischerVerkehrszweig', label: 'Domestic Transport Mode' },
    { value: 'kopf.verkehrszweigAnDerGrenze', label: 'Border Transport Mode' },
    { value: 'kopf.gesamtRohmasse', label: 'Gross Mass' },
    { value: 'kopf.massgeblichesDatum', label: 'Relevant Date' },
    { value: 'kopf.kopfDatumdesAusgangs', label: 'Export Date' },
    { value: 'kopf.zeitpunktDerGestellung', label: 'Presentation Time' },
    { value: 'kopf.zeitpunktdesEndesderLadetatigkeit', label: 'End of Loading Time' },
    { value: 'kopf.ausfuhrZollstelleFurDieErganzende', label: 'Supplementary Export Customs Office' },
    { value: 'kopf.lieferbedingungUnLocode', label: 'UN/LOCODE' },
    { value: 'kopf.lieferbedingungText', label: 'Delivery Terms Text' },
    { value: 'kopf.beforderungskostenZahlungsart', label: 'Transport Cost Payment Type' },
    { value: 'kopf.warenortBewilligungsnummer', label: 'Authorization Number' },
    { value: 'kopf.beforderungsroute.ausgewahlteLander', label: 'Selected Countries' },
    { value: 'kopf.dienststellennummer', label: 'Customs Office Number' },
    { value: 'kopf.besondereUmstande', label: 'Special Circumstances' },
    { value: 'kopf.referenznummerUCR', label: 'UCR Reference' },
    { value: 'kopf.artdesGeschafts', label: 'Type of Transaction' },
    { value: 'kopf.ausgangszollstelle', label: 'Exit Customs Office' },
    { value: 'kopf.eingangszollstelle', label: 'Entry Customs Office' },
    { value: 'kopf.verfahren', label: 'Procedure' },
    { value: 'kopf.anmeldeart', label: 'Declaration Type Code' },
    { value: 'kopf.verkehrszweig', label: 'Transport Mode' },
    { value: 'kopf.messages', label: 'Validation Messages' },

    // ===== Authorization (Bewilligung) Fields =====
    { value: 'kopf.bewilligung[0].art', label: 'Authorization Type' },
    { value: 'kopf.bewilligung[0].referenznummer', label: 'Authorization Reference Number' },

    // ===== Location of Goods (Warenort) Fields =====
    { value: 'kopf.warenortArtdesOrtes', label: 'Location Type' },
    { value: 'kopf.warenortArtDerOrtsbestimmung', label: 'Location Determination Type' },
    { value: 'kopf.warenortZusatzlicheKennung', label: 'Additional Location Identifier' },

    // ===== Transport at Departure (Beforderungsmittel beim Abgang) Fields =====
    { value: 'kopf.beforderungsmittelBeimAbgang[0].artderIdentifikation', label: 'Transport ID Type at Departure' },
    { value: 'kopf.beforderungsmittelBeimAbgang[0].kennzeichen', label: 'Vehicle Registration at Departure' },
    { value: 'kopf.beforderungsmittelBeimAbgang[0].staatszugehorigkeit', label: 'Vehicle Nationality at Departure' },

    // ===== Border Transport (Beforderungsmittel der Grenze) Fields =====
    { value: 'kopf.beforderungsmittelderGrenzeKennzeichen', label: 'Border Vehicle Registration' },

    // ===== Exporter (Ausführer) Fields =====
    { value: 'kopf.ausfuhrer.tin', label: 'Exporter TIN' },
    { value: 'kopf.ausfuhrer.name', label: 'Exporter Name' },
    { value: 'kopf.ausfuhrer.strasse', label: 'Exporter Street' },
    { value: 'kopf.ausfuhrer.plz', label: 'Exporter Postal Code' },
    { value: 'kopf.ausfuhrer.ort', label: 'Exporter City' },
    { value: 'kopf.ausfuhrer.land', label: 'Exporter Country' },
    { value: 'kopf.ausfuhrer.niederlassungsNummer', label: 'Exporter Branch Number' },
    { value: 'kopf.ausfuhrer.identifikationsart', label: 'Exporter Identification Type' },
    { value: 'kopf.ausfuhrer.telefonnummer', label: 'Exporter Phone' },
    { value: 'kopf.ausfuhrer.fax', label: 'Exporter Fax' },
    { value: 'kopf.ausfuhrer.email', label: 'Exporter Email' },
    { value: 'kopf.ausfuhrer.bundesland', label: 'Exporter Federal State' },

    // ===== Declarant (Anmelder) Fields =====
    { value: 'kopf.anmelder.tin', label: 'Declarant TIN' },
    { value: 'kopf.anmelder.name', label: 'Declarant Name' },
    { value: 'kopf.anmelder.strasse', label: 'Declarant Street' },
    { value: 'kopf.anmelder.plz', label: 'Declarant Postal Code' },
    { value: 'kopf.anmelder.ort', label: 'Declarant City' },
    { value: 'kopf.anmelder.land', label: 'Declarant Country' },
    { value: 'kopf.anmelder.niederlassungsNummer', label: 'Declarant Branch Number' },
    { value: 'kopf.anmelder.phone', label: 'Declarant Phone' },
    { value: 'kopf.anmelder.ansprechName', label: 'Declarant Contact Name' },
    { value: 'kopf.anmelder.ansprechEmail', label: 'Declarant Contact Email' },
    { value: 'kopf.anmelder.beauftragterName', label: 'Authorized Person' },
    { value: 'kopf.anmelder.identifikationsart', label: 'Declarant Identification Type' },

    // ===== Representative (Vertreter) Fields =====
    { value: 'kopf.vertreter.tin', label: 'Representative TIN' },
    { value: 'kopf.vertreter.niederlassungsNummer', label: 'Representative Branch Number' },
    { value: 'kopf.vertreter.phone', label: 'Representative Phone' },
    { value: 'kopf.vertreter.ansprechName', label: 'Representative Contact Person' },

    // ===== Subcontractor (Subunternehmer) Fields =====
    { value: 'kopf.subunternehmer.tin', label: 'Subcontractor TIN' },
    { value: 'kopf.subunternehmer.niederlassungsNummer', label: 'Subcontractor Branch Number' },

    // ===== Consignor (Versender) Fields =====
    { value: 'kopf.versender.tin', label: 'Consignor TIN' },
    { value: 'kopf.versender.name', label: 'Consignor Name' },
    { value: 'kopf.versender.strasse', label: 'Consignor Street' },
    { value: 'kopf.versender.plz', label: 'Consignor Postal Code' },
    { value: 'kopf.versender.ort', label: 'Consignor City' },
    { value: 'kopf.versender.land', label: 'Consignor Country' },

    // ===== Recipient (Empfänger) Fields =====
    { value: 'kopf.empfanger.tin', label: 'Recipient TIN' },
    { value: 'kopf.empfanger.name', label: 'Recipient Name' },
    { value: 'kopf.empfanger.strasse', label: 'Recipient Street' },
    { value: 'kopf.empfanger.plz', label: 'Recipient Postal Code' },
    { value: 'kopf.empfanger.ort', label: 'Recipient City' },
    { value: 'kopf.empfanger.land', label: 'Recipient Country' },
    { value: 'kopf.empfanger.niederlassungsNummer', label: 'Recipient Branch Number' },
    { value: 'kopf.empfanger.identifikationsart', label: 'Recipient Identification Type' },
    { value: 'kopf.empfanger.bundesland', label: 'Recipient Federal State' },

    // ===== Goods Position (Item) Fields - Position 0 =====
    { value: 'position[0].warePositionsnummer', label: 'Position Number (Item 1)' },
    { value: 'position[0].beantragtesVerfahren', label: 'Requested Procedure (Item 1)' },
    { value: 'position[0].vorhergehendesVerfahren', label: 'Previous Procedure (Item 1)' },
    { value: 'position[0].ursprungsland', label: 'Country of Origin (Item 1)' },
    { value: 'position[0].ursprungsVersendungsregion', label: 'Region of Origin (Item 1)' },
    { value: 'position[0].wareWarenbezeichnung', label: 'Product Description (Item 1)' },
    { value: 'position[0].wareWarennummerKN8', label: 'HS/CN Code (Item 1)' },
    { value: 'position[0].wareRohmasse', label: 'Gross Weight (Item 1)' },
    { value: 'position[0].wareEigenmasse', label: 'Net Weight (Item 1)' },
    { value: 'position[0].mengeinbesondererMabeinheit', label: 'Quantity in Special Unit (Item 1)' },
    { value: 'position[0].statistischerWert', label: 'Statistical Value (Item 1)' },
    { value: 'position[0].artdesGeschafts', label: 'Type of Transaction (Item 1)' },
    { value: 'position[0].ausfuhrLand', label: 'Export Country (Item 1)' },
    { value: 'position[0].referenznummerUCR', label: 'UCR Reference (Item 1)' },
    { value: 'position[0].registriernummerextern', label: 'External Registration Number (Item 1)' },
    { value: 'position[0].zusatzlichesVerfahren', label: 'Additional Procedure (Item 1)' },
    { value: 'position[0].warecusnummer', label: 'CUS Number (Item 1)' },
    { value: 'position[0].menge', label: 'Quantity (Item 1)' },
    { value: 'position[0].userid', label: 'User ID (Item 1)' },

    // ===== Packaging (Verpackung) Fields - Position 0 =====
    { value: 'position[0].verpackung[0].artderVerpackung', label: 'Package Type (Item 1)' },
    { value: 'position[0].verpackung[0].anzahlderPackstucke', label: 'Number of Packages (Item 1)' },
    { value: 'position[0].verpackung[0].versandzeichen', label: 'Shipping Marks (Item 1)' },
    { value: 'position[0].verpackung[0].packstuckverweisPositionsnummer', label: 'Package Reference Position (Item 1)' },

    // ===== Document (Unterlage) Fields - Position 0 =====
    { value: 'position[0].unterlage[0].art', label: 'Document Type (Item 1)' },
    { value: 'position[0].unterlage[0].referenznummer', label: 'Document Reference (Item 1)' },
    { value: 'position[0].unterlage[0].qualifikator', label: 'Document Qualifier (Item 1)' },
    { value: 'position[0].unterlage[0].zeilenPositionsnummer', label: 'Document Line Position (Item 1)' },
    { value: 'position[0].unterlage[0].zusatzlicheAngaben', label: 'Additional Document Info (Item 1)' },
    { value: 'position[0].unterlage[0].detail', label: 'Document Detail (Item 1)' },
    { value: 'position[0].unterlage[0].namederausstellendenBehorde', label: 'Issuing Authority (Item 1)' },
    { value: 'position[0].unterlage[0].datumderAusstellung', label: 'Issue Date (Item 1)' },
    { value: 'position[0].unterlage[0].gultigkeitsdatum', label: 'Validity Date (Item 1)' },

    // ===== Other Reference (Sonstiger Verweis) Fields - Position 0 =====
    { value: 'position[0].sonstigerVerweis[0].art', label: 'Other Reference Type (Item 1)' },
    { value: 'position[0].sonstigerVerweis[0].referenznummer', label: 'Other Reference Number (Item 1)' },
    { value: 'position[0].sonstigerVerweis[0].qualifikator', label: 'Other Reference Qualifier (Item 1)' },
    { value: 'position[0].sonstigerVerweis[0].detail', label: 'Other Reference Detail (Item 1)' },

    // ===== Goods Position (Item) Fields - Position 1 =====
    { value: 'position[1].warePositionsnummer', label: 'Position Number (Item 2)' },
    { value: 'position[1].beantragtesVerfahren', label: 'Requested Procedure (Item 2)' },
    { value: 'position[1].vorhergehendesVerfahren', label: 'Previous Procedure (Item 2)' },
    { value: 'position[1].ursprungsland', label: 'Country of Origin (Item 2)' },
    { value: 'position[1].ursprungsVersendungsregion', label: 'Region of Origin (Item 2)' },
    { value: 'position[1].wareWarenbezeichnung', label: 'Product Description (Item 2)' },
    { value: 'position[1].wareWarennummerKN8', label: 'HS/CN Code (Item 2)' },
    { value: 'position[1].wareRohmasse', label: 'Gross Weight (Item 2)' },
    { value: 'position[1].wareEigenmasse', label: 'Net Weight (Item 2)' },
    { value: 'position[1].mengeinbesondererMabeinheit', label: 'Quantity in Special Unit (Item 2)' },
    { value: 'position[1].statistischerWert', label: 'Statistical Value (Item 2)' },
    { value: 'position[1].artdesGeschafts', label: 'Type of Transaction (Item 2)' },
    { value: 'position[1].ausfuhrLand', label: 'Export Country (Item 2)' },
    { value: 'position[1].referenznummerUCR', label: 'UCR Reference (Item 2)' },
    { value: 'position[1].registriernummerextern', label: 'External Registration Number (Item 2)' },
    { value: 'position[1].zusatzlichesVerfahren', label: 'Additional Procedure (Item 2)' },
    { value: 'position[1].warecusnummer', label: 'CUS Number (Item 2)' },
    { value: 'position[1].menge', label: 'Quantity (Item 2)' },
    { value: 'position[1].userid', label: 'User ID (Item 2)' },

    // ===== Packaging (Verpackung) Fields - Position 1 =====
    { value: 'position[1].verpackung[0].artderVerpackung', label: 'Package Type (Item 2)' },
    { value: 'position[1].verpackung[0].anzahlderPackstucke', label: 'Number of Packages (Item 2)' },
    { value: 'position[1].verpackung[0].versandzeichen', label: 'Shipping Marks (Item 2)' },
    { value: 'position[1].verpackung[0].packstuckverweisPositionsnummer', label: 'Package Reference Position (Item 2)' },

    // ===== Goods Position (Item) Fields - Position 2 =====
    { value: 'position[2].warePositionsnummer', label: 'Position Number (Item 3)' },
    { value: 'position[2].beantragtesVerfahren', label: 'Requested Procedure (Item 3)' },
    { value: 'position[2].vorhergehendesVerfahren', label: 'Previous Procedure (Item 3)' },
    { value: 'position[2].ursprungsland', label: 'Country of Origin (Item 3)' },
    { value: 'position[2].ursprungsVersendungsregion', label: 'Region of Origin (Item 3)' },
    { value: 'position[2].wareWarenbezeichnung', label: 'Product Description (Item 3)' },
    { value: 'position[2].wareWarennummerKN8', label: 'HS/CN Code (Item 3)' },
    { value: 'position[2].wareRohmasse', label: 'Gross Weight (Item 3)' },
    { value: 'position[2].wareEigenmasse', label: 'Net Weight (Item 3)' },
    { value: 'position[2].mengeinbesondererMabeinheit', label: 'Quantity in Special Unit (Item 3)' },
    { value: 'position[2].statistischerWert', label: 'Statistical Value (Item 3)' },
    { value: 'position[2].artdesGeschafts', label: 'Type of Transaction (Item 3)' },
    { value: 'position[2].ausfuhrLand', label: 'Export Country (Item 3)' },
    { value: 'position[2].referenznummerUCR', label: 'UCR Reference (Item 3)' },
    { value: 'position[2].registriernummerextern', label: 'External Registration Number (Item 3)' },
    { value: 'position[2].zusatzlichesVerfahren', label: 'Additional Procedure (Item 3)' },
    { value: 'position[2].warecusnummer', label: 'CUS Number (Item 3)' },
    { value: 'position[2].menge', label: 'Quantity (Item 3)' },
    { value: 'position[2].userid', label: 'User ID (Item 3)' },

    // ===== Packaging (Verpackung) Fields - Position 2 =====
    { value: 'position[2].verpackung[0].artderVerpackung', label: 'Package Type (Item 3)' },
    { value: 'position[2].verpackung[0].anzahlderPackstucke', label: 'Number of Packages (Item 3)' },
    { value: 'position[2].verpackung[0].versandzeichen', label: 'Shipping Marks (Item 3)' },
    { value: 'position[2].verpackung[0].packstuckverweisPositionsnummer', label: 'Package Reference Position (Item 3)' },
  ];

  // Friendly label for a stored field_path (used by the restore list, which has
  // only the path). Exact match first; else the last path segment as a fallback
  // so array-indexed paths (positionen[0].warennummer) still read sensibly.
  const labelForPath = (fieldPath: string): string => {
    const exact = availableFields.find(f => f.value === fieldPath);
    if (exact) return exact.label;
    const leaf = fieldPath.split('.').pop() || fieldPath;
    return leaf;
  };

  const handleDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  const handlePageRenderSuccess = (page: any) => {
    if (page) {
      // Get the viewport with actual scale used for rendering
      // react-pdf uses scale=1 by default, but we need to check actual scale
      const viewport = page.getViewport({ scale: 1 });

      // Get PDF page dimensions in points (from PDF metadata)
      const pdfWidth = page.view[2];  // PDF width in points
      const pdfHeight = page.view[3]; // PDF height in points

      // Calculate actual scale factor
      const scale = viewport.width / pdfWidth;

      const pageInfo = {
        pdfWidth,      // PDF width in points
        pdfHeight,     // PDF height in points  
        renderWidth: viewport.width,   // Rendered width in pixels
        renderHeight: viewport.height, // Rendered height in pixels
        scale          // Scale factor (pixels per point)
      };

      console.log('Page rendered:', {
        pdfSize: `${pdfWidth}x${pdfHeight} points`,
        renderSize: `${viewport.width}x${viewport.height} px`,
        scale,
        pageInfo
      });

      setPageDimensions({
        width: viewport.width,
        height: viewport.height
      });

      // Store page info for coordinate conversion
      setPageInfo(pageInfo);
    }
  };

  // Reset page dimensions and position when page changes
  useEffect(() => {
    setPageDimensions(null);
    setPagePosition(null);
    setPageInfo(null);
    setBoundingBox(null);
    setStartPoint(null);
    setIsDrawing(false);
  }, [currentPage]);

  // Log when draw mode changes
  useEffect(() => {
    console.log('Draw mode changed:', { drawMode, pageDimensions, pageInfo });
  }, [drawMode, pageDimensions, pageInfo]);

  const handleDocumentLoadError = (error: Error) => {
    console.error('Failed to load PDF document:', error);
    console.error('Error details:', {
      message: error.message,
      name: error.name,
      stack: error.stack
    });
    console.error('PDF.js worker config:', {
      workerSrc: pdfjs.GlobalWorkerOptions.workerSrc,
      version: pdfjs.version
    });
    setError(`Failed to load PDF document: ${error.message}. Check browser console for details.`);
  };

  // Shared point-in-box test in PDF points, used by both click hit-testing
  // and hover. Handles negative width/height (boxes drawn right-to-left).
  const pointInBox = (box: SavedBoundingBox, px: number, py: number) => {
    if (box.pageNumber !== currentPage) return false;
    const left = Math.min(box.x, box.x + box.width);
    const top = Math.min(box.y, box.y + box.height);
    return (
      px >= left && px <= left + Math.abs(box.width) &&
      py >= top && py <= top + Math.abs(box.height)
    );
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    console.log('🖱️ Mouse down event triggered');
    if (!canvasRef.current || !pageInfo) {
      console.log('❌ No canvas or pageInfo');
      return;
    }

    // Get the canvas position
    const canvasRect = canvasRef.current.getBoundingClientRect();

    // Calculate position relative to the canvas (in pixels)
    const pixelX = e.clientX - canvasRect.left;
    const pixelY = e.clientY - canvasRect.top;

    // Convert from pixels to PDF points using scale factor
    const scale = pageInfo.scale;
    const pdfX = pixelX / scale;
    const pdfY = pixelY / scale;

    console.log('🖱️ Click position:', { pixelX, pixelY, pdfX, pdfY, scale });
    console.log('🖱️ Checking against', savedBoxes.length, 'saved boxes');

    // Check if the click hit a box (for select → delete). Both mapped rule
    // boxes and extracted (X-Ray) boxes are selectable; only the currently
    // visible layers are hit-tested, and mapped wins when they overlap.
    const hitTest = (box: SavedBoundingBox) => pointInBox(box, pdfX, pdfY);
    // In draw mode, a click always starts a draw — box selection must NOT
    // pre-empt it, otherwise you can never draw over an existing extracted
    // box (which cover most values), breaking the re-map/correct flow.
    const clickedBox: SavedBoundingBox | undefined = drawMode
      ? undefined
      : (showMappedFields ? savedBoxes.find(hitTest) : undefined) ||
        (showXRay ? xrayBoxes.find(hitTest) : undefined);

    if (clickedBox) {
      // Select the box for deletion
      console.log('✅ Box clicked! Setting selectedBox:', clickedBox);
      setSelectedBox(clickedBox);
      console.log('📦 Selected box for deletion:', clickedBox);
      return;
    } else {
      console.log('❌ No box clicked at this position');
    }

    // If not clicking on a box and in draw mode, start drawing
    if (drawMode) {
      // Debug logging
      console.log('Mouse down:', {
        client: { x: e.clientX, y: e.clientY },
        canvas: { left: canvasRect.left, top: canvasRect.top, width: canvasRect.width, height: canvasRect.height },
        pixel: { x: pixelX, y: pixelY },
        pdf: { x: pdfX, y: pdfY },
        scale,
        pageInfo
      });

      // Ensure coordinates are within the PDF bounds (in points)
      if (pdfX >= 0 && pdfY >= 0 && pdfX <= pageInfo.pdfWidth && pdfY <= pageInfo.pdfHeight) {
        setIsDrawing(true);
        // Store PDF point coordinates
        setStartPoint({ x: pdfX, y: pdfY });
        setBoundingBox(null);
        setSelectedBox(null); // Clear selection when starting new draw
      } else {
        console.log('Coordinates out of bounds:', { pdfX, pdfY, pdfWidth: pageInfo.pdfWidth, pdfHeight: pageInfo.pdfHeight });
      }
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current || !pageInfo) return;

    // Get position relative to canvas
    const canvasRect = canvasRef.current.getBoundingClientRect();
    const pixelX = e.clientX - canvasRect.left;
    const pixelY = e.clientY - canvasRect.top;
    const scale = pageInfo.scale;
    const pdfX = pixelX / scale;
    const pdfY = pixelY / scale;

    // Bi-directional hover across BOTH visible layers (mapped + extracted):
    // sets the pointer cursor and the highlighted fieldPath (list↔canvas).
    // Mapped wins when they overlap. Skipped while drawing.
    const hovHit = (box: SavedBoundingBox) => pointInBox(box, pdfX, pdfY);
    // Only compute hover when not actively drawing — otherwise we scan both
    // box arrays and write state on every mousemove during a drag for nothing.
    if (!isDrawing) {
      const hovered: SavedBoundingBox | undefined =
        (showMappedFields ? savedBoxes.find(hovHit) : undefined) ||
        (showXRay ? xrayBoxes.find(hovHit) : undefined);
      setIsHoveringBox(!!hovered);
      setHoveredFieldPath(hovered ? hovered.fieldPath : null);
    }

    // Handle draw-move only when actively drawing
    if (!isDrawing || !startPoint) return;

    const currentPdfX = pdfX;
    const currentPdfY = pdfY;
    const width = currentPdfX - startPoint.x;
    const height = currentPdfY - startPoint.y;

    console.log('Mouse move:', { pixel: { x: pixelX, y: pixelY }, pdf: { x: currentPdfX, y: currentPdfY }, start: startPoint, width, height, scale });

    setBoundingBox({
      x: startPoint.x,
      y: startPoint.y,
      width,
      height,
      pageNumber: currentPage,
    });
  };

  // Snap a freehand-drawn region to the union of the point-cloud word boxes it
  // overlaps, and return their box_ids. Gives clean, OCR-aligned rectangles and
  // an ID-first anchor for the saved rule. No-op (returns the box unchanged,
  // ids=[]) when there's no point cloud, so pre-grounding docs still work.
  const snapToWords = (
    box: BoundingBox
  ): { box: BoundingBox; ids: string[] } => {
    if (pointCloud.length === 0) return { box, ids: [] };
    const left = Math.min(box.x, box.x + box.width);
    const top = Math.min(box.y, box.y + box.height);
    const right = left + Math.abs(box.width);
    const bottom = top + Math.abs(box.height);

    const hits = pointCloud.filter(w => {
      // Snap only words whose CENTRE falls inside the drawn region. Using the
      // centre (not any-overlap) means a box that merely clips a neighbouring
      // word doesn't drag that whole word into the union and balloon the box.
      const cx = w.x + w.width / 2;
      const cy = w.y + w.height / 2;
      return cx >= left && cx <= right && cy >= top && cy <= bottom;
    });
    if (hits.length === 0) return { box, ids: [] };

    const nx = Math.min(...hits.map(w => w.x));
    const ny = Math.min(...hits.map(w => w.y));
    const nr = Math.max(...hits.map(w => w.x + w.width));
    const nb = Math.max(...hits.map(w => w.y + w.height));
    return {
      box: { x: nx, y: ny, width: nr - nx, height: nb - ny, pageNumber: box.pageNumber },
      ids: hits.map(w => w.id),
    };
  };

  const handleMouseUp = async () => {
    if (!isDrawing) return;
    setIsDrawing(false);

    if (boundingBox && Math.abs(boundingBox.width) > 10 && Math.abs(boundingBox.height) > 10) {
      // Snap the drawn region to the underlying OCR words (Ghost Layer), then
      // extract from the snapped rectangle. Keeps the raw box if nothing snaps.
      const { box: snapped, ids } = snapToWords(boundingBox);
      setSnappedBoxIds(ids);
      const regionBox = ids.length > 0 ? snapped : boundingBox;
      if (ids.length > 0) {
        setBoundingBox(snapped);
        console.log(`🧲 Snapped to ${ids.length} word(s): ${ids.join(', ')}`);
      }
      await extractTextFromRegion(regionBox);
    }
  };

  const extractTextFromRegion = async (box: BoundingBox) => {
    setIsExtracting(true);
    setError('');

    try {
      // Check if user is authenticated
      const { token } = useAuthStore.getState();
      console.log('🔐 AUTH CHECK - Teach AI:', {
        hasToken: !!token,
        hasAccessToken: !!token?.access_token,
        tokenLength: token?.access_token?.length,
        tokenPreview: token?.access_token ? `${token.access_token.substring(0, 10)}...` : 'NO TOKEN'
      });

      // DEBUG: Also check localStorage as backup
      const localStorageToken = localStorage.getItem('access_token');
      console.log('🔐 LOCALSTORAGE CHECK:', {
        hasLocalStorageToken: !!localStorageToken,
        localStorageTokenLength: localStorageToken?.length,
        localStorageTokenPreview: localStorageToken ? `${localStorageToken.substring(0, 10)}...` : 'NO TOKEN'
      });

      if (!token?.access_token) {
        console.error('❌ NO AUTH TOKEN FOUND in auth store!');
        throw new Error('Authentication required. Please log in.');
      }

      // Normalize coordinates in PDF points (ensure x, y are top-left corner)
      const normalizedX = Math.min(box.x, box.x + box.width);
      const normalizedY = Math.min(box.y, box.y + box.height);
      const normalizedWidth = Math.abs(box.width);
      const normalizedHeight = Math.abs(box.height);

      // Validate coordinates
      if (normalizedWidth < 10 || normalizedHeight < 10) {
        throw new Error('Selection too small. Please draw a larger area.');
      }

      if (!pageInfo?.pdfWidth || !pageInfo?.pdfHeight) {
        throw new Error('PDF dimensions not available. Please wait for page to load.');
      }

      if (normalizedX < 0 || normalizedY < 0 ||
        normalizedX + normalizedWidth > pageInfo.pdfWidth ||
        normalizedY + normalizedHeight > pageInfo.pdfHeight) {
        throw new Error('Selection is outside PDF page bounds.');
      }

      // Send PDF point coordinates to backend using api instance
      console.log('🚀 Making API call using api instance to /api/v1/teach-ai/extract-region', {
        fileId,
        pageNumber: box.pageNumber,
        pageInfo,
        normalizedCoordinates: {
          x: normalizedX,
          y: normalizedY,
          width: normalizedWidth,
          height: normalizedHeight,
          withinBounds: normalizedX >= 0 && normalizedY >= 0 &&
            normalizedX + normalizedWidth <= pageInfo.pdfWidth &&
            normalizedY + normalizedHeight <= pageInfo.pdfHeight
        },
        pdfBounds: { width: pageInfo.pdfWidth, height: pageInfo.pdfHeight },
        usingApiInstance: true
      });

      try {
        // Try using the api instance (handles auth automatically)
        const response = await api.post('/api/v1/teach-ai/extract-region', {
          file_id: fileId,
          page_number: box.pageNumber,
          bounding_box: {
            x: normalizedX,           // PDF points
            y: normalizedY,           // PDF points
            width: normalizedWidth,   // PDF points
            height: normalizedHeight, // PDF points
            // Send PDF dimensions as canvas dimensions for scaling
            canvas_width: pageInfo?.pdfWidth || 0,
            canvas_height: pageInfo?.pdfHeight || 0,
          },
        });

        console.log('✅ API call successful:', response.status);
        setExtractedText(response.data.extracted_text);
        setShowFieldMapper(true);
      } catch (apiError: any) {
        console.error('❌ API instance call failed:', apiError);

        // Fallback to fetch if api instance fails
        console.log('🔄 Trying fallback with fetch...');
        const fetchResponse = await fetch('/api/v1/teach-ai/extract-region', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token.access_token}`,
          },
          body: JSON.stringify({
            file_id: fileId,
            page_number: box.pageNumber,
            bounding_box: {
              x: normalizedX,
              y: normalizedY,
              width: normalizedWidth,
              height: normalizedHeight,
              canvas_width: pageInfo?.pdfWidth || 0,
              canvas_height: pageInfo?.pdfHeight || 0,
            },
          }),
        });

        if (!fetchResponse.ok) {
          let errorText = 'No error message';
          try {
            errorText = await fetchResponse.text();
          } catch (e) {
            console.log('Could not read error response body:', e);
          }

          console.error('❌ Fetch fallback also failed:', {
            status: fetchResponse.status,
            statusText: fetchResponse.statusText,
            error: errorText,
            headers: Array.from(fetchResponse.headers.entries())
          });

          if (fetchResponse.status === 401) {
            throw new Error('Authentication failed. The server returned 401 Unauthorized.');
          } else if (fetchResponse.status === 500) {
            // Try to parse JSON error if available
            let errorDetail = 'Internal server error';
            try {
              const errorJson = JSON.parse(errorText);
              errorDetail = errorJson.detail || errorJson.message || errorText;
            } catch {
              errorDetail = errorText || 'Internal server error (check backend logs)';
            }
            throw new Error(`Server error: ${errorDetail}`);
          }
          throw new Error(`Failed to extract text: ${fetchResponse.status} ${fetchResponse.statusText}`);
        }

        const data = await fetchResponse.json();
        setExtractedText(data.extracted_text);
        setShowFieldMapper(true);
      }
    } catch (err: any) {
      console.error('Extraction error details:', err);

      let errorMessage = 'Failed to extract text from the selected region';

      if (err?.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setIsExtracting(false);
    }
  };

  const handleFieldMapping = async () => {
    console.log('🔄 TeachAIModal: handleFieldMapping called', {
      selectedField,
      extractedText,
      hasSelectedField: !!selectedField,
      hasExtractedText: !!extractedText
    });

    if (!selectedField || !extractedText || !boundingBox) {
      console.log('❌ TeachAIModal: Cannot map field - missing selectedField, extractedText, or boundingBox');
      return;
    }

    console.log('✅ TeachAIModal: Calling onFieldMapped with:', { selectedField, extractedText });

    // Call the parent callback to update the field immediately
    onFieldMapped(selectedField, extractedText);

    // Save the bounding box with field mapping
    const fieldLabel = availableFields.find(f => f.value === selectedField)?.label || selectedField;
    const savedBox: SavedBoundingBox = {
      ...boundingBox,
      fieldPath: selectedField,
      fieldLabel: fieldLabel,
      extractedText: extractedText,
      source: 'mapped',
    };
    console.log('📦 About to save bounding box:', savedBox);
    // A re-map supersedes the field: replace any existing mapped box for this
    // field ON THIS PAGE, and drop the AI/extracted (X-Ray) box for it so the
    // overlay shows the single corrected box, not two truths for the same
    // field. Scope to the page so a future multi-page merge can't nuke another
    // page's mapping for the same field path.
    const supersedes = (b: SavedBoundingBox) =>
      b.fieldPath === selectedField && b.pageNumber === savedBox.pageNumber;
    setSavedBoxes(prev => [...prev.filter(b => !supersedes(b)), savedBox]);
    setXrayBoxes(prev => prev.filter(b => !supersedes(b)));

    // Save the rule for future extractions. The backend generates an NLP
    // positional rule from the bounding box via mistral-medium-3-5, which
    // takes 2–6s — so we surface a loading toast and report success/failure.
    setIsSavingRule(true);
    const savingToastId = toast.loading('Teaching AI this field...', {
      position: 'top-right',
    });
    try {
      const normalizedX = Math.min(boundingBox.x, boundingBox.x + boundingBox.width);
      const normalizedY = Math.min(boundingBox.y, boundingBox.y + boundingBox.height);
      const normalizedWidth = Math.abs(boundingBox.width);
      const normalizedHeight = Math.abs(boundingBox.height);

      const response = await api.post('/api/v1/teach-ai/save-rule', {
        file_id: fileId,
        field_path: selectedField,
        page_number: boundingBox.pageNumber,
        bounding_box: {
          x: normalizedX,
          y: normalizedY,
          width: normalizedWidth,
          height: normalizedHeight,
          canvas_width: pageInfo?.pdfWidth || 0,
          canvas_height: pageInfo?.pdfHeight || 0,
        },
        // Ghost Layer: box_ids the region snapped to, so the rule can be
        // ID-anchored for future ID-first grounding (accepted by save-rule via
        // its extra=allow config). Empty when nothing snapped.
        source_box: snappedBoxIds,
      });

      console.log('✅ Rule saved successfully:', response.data);
      toast.update(savingToastId, {
        render: `Rule saved for ${response.data.vendor_name || 'this vendor'}`,
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
    } catch (ruleError: any) {
      console.error('⚠️ Failed to save rule (field value still applied locally):', ruleError);
      const detail =
        ruleError?.response?.data?.detail || ruleError?.message || 'unknown error';
      toast.update(savingToastId, {
        render: `Rule not saved (${detail}). Field value still applied — try again later.`,
        type: 'error',
        isLoading: false,
        autoClose: 5000,
      });
    } finally {
      setIsSavingRule(false);
    }

    // Reset state (keep drawMode true to show saved boxes)
    setBoundingBox(null);
    setExtractedText('');
    setShowFieldMapper(false);
    setSelectedField('');
    setSnappedBoxIds([]);
    // Don't turn off draw mode - user can continue mapping or manually toggle off

    console.log('✅ TeachAIModal: Field mapping completed, state reset');
  };

  // Deleting a rule (mapped) or suppressing a field (extracted) is destructive
  // and persists a vendor-wide change, so route both through a confirmation
  // dialog instead of firing immediately. The actual work is in performDelete.
  const handleDeleteSelectedBox = (boxArg?: SavedBoundingBox) => {
    const box = boxArg || selectedBox;
    if (!box) return;
    setConfirmDelete(box);
  };

  const performDelete = async (box: SavedBoundingBox) => {
    const { fieldPath, fieldLabel, pageNumber, source } = box;
    setIsDeleting(true);

    // Reconcile BOTH overlays + the live form for this field (two-truths fix:
    // the same field may appear as a mapped rule box AND an extracted box).
    const clearField = () => {
      setSavedBoxes(prev => prev.filter(b => b.fieldPath !== fieldPath));
      setXrayBoxes(prev => prev.filter(b => b.fieldPath !== fieldPath));
      if (onFieldDeleted) onFieldDeleted(fieldPath);
    };

    // Extracted (X-Ray) box: deleting it SUPPRESSES the field — persist a
    // per-vendor rule so the AI stops extracting it on future uploads of the
    // same template. Clear the form only on success so a failed save doesn't
    // silently drop the value. (To correct instead, draw a new box — Slice C.)
    if (source === 'extracted') {
      try {
        await api.post('/api/v1/teach-ai/suppress-field', {
          file_id: fileId,
          field_path: fieldPath,
          page_number: pageNumber,
          suppressed_value: box.extractedText || undefined,
        });
        clearField();
        // Field is now restorable — refresh the restore list.
        loadSuppressedFields();
        toast.success(
          `Suppressed "${fieldLabel}" — won't be extracted from this vendor next time`,
          { position: 'top-right', autoClose: 3500 }
        );
      } catch (err: any) {
        console.error('❌ Error suppressing field:', err);
        const errorMessage =
          err.response?.data?.detail || err.message || 'Failed to suppress field';
        toast.error(`Failed to suppress: ${errorMessage}`, {
          position: 'top-right',
          autoClose: 5000,
        });
      } finally {
        setSelectedBox(null);
        setConfirmDelete(null);
        setIsDeleting(false);
      }
      return;
    }

    // Mapped box is backed by a saved rule — delete it server-side first so a
    // failure doesn't silently drop the field from the form.
    try {
      const response = await api.post('/api/v1/teach-ai/delete-rule', {
        file_id: fileId,
        field_path: fieldPath,
        page_number: pageNumber,
      });
      console.log('✅ Rule deleted successfully:', response.data);
      clearField();
      toast.success(`Deleted rule for "${fieldLabel}"`, {
        position: 'top-right',
        autoClose: 3000,
      });
    } catch (err: any) {
      console.error('❌ Error deleting rule:', err);
      const errorMessage =
        err.response?.data?.detail || err.message || 'Failed to delete rule';
      toast.error(`Failed to delete rule: ${errorMessage}`, {
        position: 'top-right',
        autoClose: 5000,
      });
    } finally {
      setSelectedBox(null);
      setConfirmDelete(null);
      setIsDeleting(false);
    }
  };

  // Restore (un-suppress) a single field: remove the vendor's suppress rule,
  // re-populate the live form with the value we stored when it was deleted, and
  // reload the X-Ray so the box reappears if it still grounds on this document.
  const handleRestoreField = async (fieldPath: string, suppressedValue: string | null) => {
    setRestoringPath(fieldPath);
    try {
      const response = await api.post('/api/v1/teach-ai/restore-field', {
        file_id: fileId,
        field_path: fieldPath,
      });
      const value = response.data?.restored_value ?? suppressedValue ?? '';
      if (onFieldRestored) onFieldRestored(fieldPath, value);
      setSuppressedFields(prev => prev.filter(f => f.fieldPath !== fieldPath));
      // Bring the orange box back if the field still grounds in this doc.
      loadXRay();
      toast.success(`Restored "${labelForPath(fieldPath)}"`, {
        position: 'top-right',
        autoClose: 3000,
      });
    } catch (err: any) {
      console.error('❌ Error restoring field:', err);
      const errorMessage =
        err.response?.data?.detail || err.message || 'Failed to restore field';
      toast.error(`Failed to restore: ${errorMessage}`, {
        position: 'top-right',
        autoClose: 5000,
      });
    } finally {
      setRestoringPath(null);
    }
  };

  const handleResetRules = async () => {
    setIsResettingRules(true);
    setError('');

    try {
      console.log('🗑️ Resetting rules for file:', fileId);

      const response = await api.post('/api/v1/teach-ai/delete-rules', {
        file_id: fileId
      });

      console.log('✅ Rules reset successfully:', response.data);

      // Clear saved boxes in UI AND clear their values on the live form
      // (bulk reset previously left the form showing stale mapped values).
      const clearedPaths = Array.from(new Set(savedBoxes.map(b => b.fieldPath)));
      setSavedBoxes([]);
      if (onFieldDeleted) clearedPaths.forEach(fp => onFieldDeleted(fp));

      // Show success toast notification
      toast.success(
        `Successfully deleted ${response.data.deleted_count} rule(s) for vendor: ${response.data.vendor_name}`,
        {
          position: 'top-right',
          autoClose: 5000,
        }
      );

    } catch (err: any) {
      console.error('❌ Error resetting rules:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to reset rules';

      // Show error toast notification
      toast.error(`Failed to reset rules: ${errorMessage}`, {
        position: 'top-right',
        autoClose: 7000,
      });
    } finally {
      setIsResettingRules(false);
      setShowResetConfirm(false);
    }
  };

  // Add keyboard handler for Delete key
  useEffect(() => {
    console.log('🔑 Keyboard handler registered, selectedBox:', selectedBox);
    const handleKeyDown = (e: KeyboardEvent) => {
      console.log('⌨️ Key pressed:', e.key, 'selectedBox:', selectedBox);
      // Don't hijack Delete/Backspace while the user is typing in a field —
      // otherwise editing text (e.g. the file search or field autocomplete)
      // would eat the keystroke AND silently delete the selected field.
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) {
        return;
      }
      // Keyboard delete only removes MAPPED rule boxes. Deleting an extracted
      // box persists a vendor-wide suppress rule (destructive; undoable via
      // "Restore deleted"), so that requires an explicit trash click — an
      // accidental keypress must not open a suppress. Both now go through the
      // confirmation dialog before any server call.
      if (
        (e.key === 'Delete' || e.key === 'Backspace') &&
        selectedBox &&
        selectedBox.source === 'mapped'
      ) {
        e.preventDefault();
        console.log('🗑️ Calling handleDeleteSelectedBox from keyboard');
        handleDeleteSelectedBox();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedBox]);

  // Draw bounding box on canvas
  useEffect(() => {
    console.log('🎨 Canvas useEffect triggered', {
      hasCanvas: !!canvasRef.current,
      hasPageInfo: !!pageInfo,
      savedBoxesCount: savedBoxes.length,
      currentPage,
      boundingBox
    });

    if (!canvasRef.current) {
      console.log('❌ No canvas ref');
      return;
    }

    if (!pageInfo) {
      console.log('❌ No pageInfo');
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.log('❌ No canvas context');
      return;
    }

    console.log('✅ Canvas ready, drawing...');

    // Set canvas dimensions to match rendered PDF size
    canvas.width = pageInfo.renderWidth;
    canvas.height = pageInfo.renderHeight;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const scale = pageInfo.scale;

    // Helper: convert hex #rrggbb + alpha → rgba(r,g,b,a)
    const hexToRgba = (hex: string, alpha: number): string => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r},${g},${b},${alpha})`;
    };

    // Helper function to draw a box
    const drawBox = (
      box: BoundingBox | SavedBoundingBox,
      color: string,
      label?: string,
      isSaved: boolean = false,
      faint: boolean = false,
      tentative: boolean = false
    ) => {
      if (box.pageNumber !== currentPage) return;

      const isSelected = selectedBox && 'fieldPath' in box && 'fieldPath' in selectedBox &&
        box.fieldPath === selectedBox.fieldPath;
      // Bi-directional hover emphasis: the box whose field is hovered (here or
      // in the field list) is drawn thicker / more filled.
      const isHovered = !!hoveredFieldPath && 'fieldPath' in box &&
        (box as SavedBoundingBox).fieldPath === hoveredFieldPath;

      const pdfX = box.x;
      const pdfY = box.y;
      const pdfWidth = box.width;
      const pdfHeight = box.height;

      const drawPdfX = pdfWidth >= 0 ? pdfX : pdfX + pdfWidth;
      const drawPdfY = pdfHeight >= 0 ? pdfY : pdfY + pdfHeight;
      const drawPdfWidth = Math.abs(pdfWidth);
      const drawPdfHeight = Math.abs(pdfHeight);

      const drawPixelX = drawPdfX * scale;
      const drawPixelY = drawPdfY * scale;
      const drawPixelWidth = drawPdfWidth * scale;
      const drawPixelHeight = drawPdfHeight * scale;

      const active = isSelected || isHovered;
      const activeColor = isSelected ? '#f44336' : color;
      ctx.strokeStyle = activeColor;
      // Faint (X-Ray) boxes are a thin outline map until hovered/selected, so a
      // page full of them reads as a quiet overlay instead of a wall of boxes.
      // `tentative` (fuzzy-fallback / MATCHED) boxes read as approximate:
      // fainter + dashed, so a guessed location doesn't masquerade as a
      // confidently-located (CITED) field.
      ctx.globalAlpha = faint && !active ? (tentative ? 0.22 : 0.4) : 1;
      ctx.lineWidth = active ? 3 : (faint ? 1 : 2);
      ctx.setLineDash(!active && tentative ? [4, 3] : isSaved ? [] : [5, 5]);
      ctx.strokeRect(drawPixelX, drawPixelY, drawPixelWidth, drawPixelHeight);

      // Fill - derive from the border color. Faint boxes get no fill unless active.
      const fillAlpha = isSelected
        ? 0.2
        : isHovered
        ? 0.3
        : faint
        ? 0
        : isSaved
        ? 0.15
        : 0.1;
      if (fillAlpha > 0) {
        ctx.fillStyle = isSelected ? 'rgba(244,67,54,0.2)' : hexToRgba(color, fillAlpha);
        ctx.fillRect(drawPixelX, drawPixelY, drawPixelWidth, drawPixelHeight);
      }
      ctx.globalAlpha = 1;

      // Label badge — always for non-faint saved boxes; for faint (X-Ray)
      // boxes only when the box is hovered/selected, so the map stays clean.
      if (label && (active || (isSaved && !faint))) {
        const padding = 4;
        const fontSize = 12;
        ctx.font = `bold ${fontSize}px Arial`;
        const textWidth = ctx.measureText(label).width;
        const textHeight = fontSize;

        let labelX = drawPixelX;
        let labelY = drawPixelY - textHeight - padding * 2;
        if (labelY < 0) labelY = drawPixelY + padding;

        const badgeColor = isSelected ? 'rgba(244,67,54,0.95)' : hexToRgba(color, 0.95);
        ctx.fillStyle = badgeColor;
        ctx.fillRect(labelX, labelY, textWidth + padding * 2, textHeight + padding * 2);
        ctx.fillStyle = '#ffffff';
        ctx.textBaseline = 'top';
        ctx.fillText(label, labelX + padding, labelY + padding);
      }
    };

    // 1b. Draw X-Ray boxes coloured by grounding state (Ghost Layer)
    if (showXRay) {
      const pageXray = xrayBoxes.filter(box => box.pageNumber === currentPage);
      // Draw the hovered box LAST so it paints on top of its neighbours.
      const ordered = [
        ...pageXray.filter(b => b.fieldPath !== hoveredFieldPath),
        ...pageXray.filter(b => b.fieldPath === hoveredFieldPath),
      ];
      ordered.forEach(box =>
        drawBox(
          box,
          GROUNDING_COLORS[box.groundingState] || '#f59e0b',
          box.fieldLabel,
          true,
          true,
          box.groundingState === 'fallback' // tentative -> dashed + fainter
        )
      );
    }

    // 2. Draw saved/mapped rule boxes — indigo, distinct from extracted orange.
    // Same faint-map + bright-on-hover treatment as the extracted layer; hovered
    // box drawn last so it paints on top.
    if (showMappedFields) {
      const pageMapped = savedBoxes.filter(box => box.pageNumber === currentPage);
      const ordered = [
        ...pageMapped.filter(b => b.fieldPath !== hoveredFieldPath),
        ...pageMapped.filter(b => b.fieldPath === hoveredFieldPath),
      ];
      ordered.forEach(b => drawBox(b, '#6c63ff', b.fieldLabel, true, true));
    }

    // 3. Draw current in-progress drawing box — warm amber dashed — top layer
    if (boundingBox) {
      drawBox(boundingBox, '#f59e0b', undefined, false);
    }
  }, [boundingBox, savedBoxes, showMappedFields, xrayBoxes, showXRay, xrayAvailable, hoveredFieldPath, pageInfo, currentPage, selectedBox]);

  // ── colour tokens — derived from user theme ─
  const ACCENT = colors.primary;
  const ACCENT2 = colors.secondary;
  const PANEL_BG = isDarkMode ? colors.primaryDark : theme.palette.background.paper;
  const PANEL_BG2 = isDarkMode ? colors.primaryDark : theme.palette.background.default;
  const BORDER = isDarkMode ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.12)';
  const GREEN = theme.palette.success?.main || '#22c55e';
  const RED_DEL = theme.palette.error?.main || '#ef4444';
  const PDF_AREA = isDarkMode ? '#2a2a2a' : '#eae9e4';
  const TEXT_PRIMARY = isDarkMode ? '#e0e0e0' : '#1e1b3a';
  const TEXT_SECONDARY = isDarkMode ? '#94a3b8' : '#64748b';
  const TEXT_MUTED = isDarkMode ? '#64748b' : '#94a3b8';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: '95vw',
          maxWidth: '1400px',
          height: '92vh',
          maxHeight: '92vh',
          bgcolor: PANEL_BG,
          color: TEXT_PRIMARY,
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${ACCENT}40`,
          boxShadow: `0 8px 40px ${ACCENT}26, 0 2px 8px rgba(0,0,0,0.1)`,
        },
      }}
    >
      {/* ── TOP BAR ─────────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2.5,
          py: 1.5,
          background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT2} 100%)`,
          borderBottom: `1px solid ${ACCENT}33`,
          flexShrink: 0,
        }}
      >
        {/* Brand icon + title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              width: 32, height: 32, borderRadius: 1.5,
              background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT2})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <DrawIcon sx={{ fontSize: 18, color: '#fff' }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} lineHeight={1.1} sx={{ color: '#ffffff' }}>
              {tString('teachAI.title')}
            </Typography>
            {availableFiles && availableFiles.length > 1 ? (
              // Multi-file picker — switches the active file without closing the modal.
              // Calls onFileChange so the parent updates the file props (pdfUrl,
              // fileId, fileName, extractedData) which retriggers our reload effects.
              <FormControl size="small" sx={{ mt: 0.3, minWidth: 200, maxWidth: 360 }}>
                <Select
                  value={fileId}
                  onChange={(e: { target: { value: unknown } }) =>
                    onFileChange?.(e.target.value as string)
                  }
                  variant="standard"
                  disableUnderline
                  sx={{
                    color: 'rgba(255,255,255,0.9)',
                    fontSize: '0.75rem',
                    '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.7)' },
                    '& .MuiSelect-select': { padding: '2px 24px 2px 0' },
                  }}
                  MenuProps={{
                    PaperProps: { sx: { maxHeight: 320 } },
                  }}
                >
                  {availableFiles.map((f: { fileId: string; name: string }) => (
                    <MenuItem key={f.fileId} value={f.fileId} sx={{ fontSize: '0.85rem' }}>
                      {f.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Typography
                variant="caption"
                sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 320 }}
              >
                {fileName}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Mode toggle pill */}
        <Box
          sx={{
            display: 'flex',
            bgcolor: 'rgba(255,255,255,0.15)',
            borderRadius: 2,
            p: 0.5,
            gap: 0.5,
          }}
        >
          {[
            { 
              label: tString('teachAI.viewMode'), 
              key: 'view', 
              active: !drawMode && showMappedFields, 
              icon: <ViewIcon sx={{ fontSize: 16 }} />, 
              onClick: () => {
                if (drawMode) {
                  setDrawMode(false);
                  setShowMappedFields(true);
                } else {
                  setShowMappedFields(show => !show);
                }
              } 
            },
            // X-Ray toggle only appears once an enriched payload exists for the file
            ...(xrayAvailable
              ? [{ label: 'Extracted Fields', key: 'xray', active: showXRay, icon: <XRayIcon sx={{ fontSize: 16 }} />, onClick: () => setShowXRay(v => !v) }]
              : []),
            {
              label: tString('teachAI.drawMode'),
              key: 'draw',
              active: drawMode,
              icon: <DrawIcon sx={{ fontSize: 16 }} />,
              onClick: () => setDrawMode(true)
            },
          ].map(btn => (
            <Button
              key={btn.key}
              size="small"
              onClick={btn.onClick}
              startIcon={btn.icon}
              sx={{
                px: 1.5, py: 0.5, borderRadius: 1.5, fontSize: 12, fontWeight: 600,
                minWidth: 80,
                color: btn.active ? '#fff' : 'rgba(255,255,255,0.7)',
                bgcolor: btn.active
                  ? (btn.key === 'draw' ? 'rgba(251,146,60,0.35)' : btn.key === 'xray' ? `${ACCENT}59` : 'rgba(255,255,255,0.25)')
                  : 'transparent',
                border: btn.active
                  ? `1px solid ${btn.key === 'draw' ? 'rgba(251,146,60,0.6)' : btn.key === 'xray' ? `${ACCENT}99` : 'rgba(255,255,255,0.5)'}`
                  : '1px solid transparent',
                transition: 'all 0.2s',
                '&:hover': { bgcolor: btn.active ? undefined : 'rgba(255,255,255,0.12)' },
              }}
            >
              {btn.label}
            </Button>
          ))}
        </Box>

        {/* Delete button removed from header — now lives inline on each rule card */}

        {/* Restore deleted (suppressed) fields — separate from the destructive
            Reset, which wipes ALL rules. This only brings back suppressions. */}
        <Button
          size="small"
          startIcon={<RestoreIcon sx={{ fontSize: 16 }} />}
          onClick={() => setShowRestoreList(true)}
          disabled={suppressedFields.length === 0}
          sx={{
            color: 'rgba(255,255,255,0.75)', fontSize: 12, px: 1.5, borderRadius: 1.5,
            border: '1px solid rgba(255,255,255,0.3)',
            '&:hover': { color: '#fff', borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
            '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)', borderColor: 'rgba(255,255,255,0.1)' },
            transition: 'all 0.2s',
          }}
        >
          Restore deleted{suppressedFields.length > 0 ? ` (${suppressedFields.length})` : ''}
        </Button>

        {/* Reset + close */}
        <Button
          size="small"
          onClick={() => setShowResetConfirm(true)}
          disabled={isResettingRules || savedBoxes.length === 0}
          sx={{
            color: 'rgba(255,255,255,0.75)', fontSize: 12, px: 1.5, borderRadius: 1.5,
            border: '1px solid rgba(255,255,255,0.3)',
            '&:hover': { color: '#fff', borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
            '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)', borderColor: 'rgba(255,255,255,0.1)' },
            transition: 'all 0.2s',
          }}
        >
          {tString('teachAI.resetRules')}
        </Button>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.12)' } }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* ── MAIN BODY ───────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* LEFT: PDF viewer */}
        <Box
          ref={containerRef}
          sx={{
            flex: 1,
            overflow: 'auto',
            bgcolor: PDF_AREA,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            position: 'relative',
            outline: drawMode ? '2px solid rgba(251,146,60,0.55)' : '2px solid transparent',
            transition: 'outline 0.3s',
          }}
        >
          {/* Draw-mode banner */}
          {drawMode && (
            <Box
              sx={{
                position: 'sticky', top: 0, zIndex: 20, width: '100%',
                py: 0.75, px: 2,
                bgcolor: 'rgba(251,146,60,0.12)',
                borderBottom: '1px solid rgba(251,146,60,0.3)',
                display: 'flex', alignItems: 'center', gap: 1,
              }}
            >
              <Box sx={{
                width: 8, height: 8, borderRadius: '50%', bgcolor: '#fb923c',
                animation: 'blink 1s infinite',
                '@keyframes blink': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.3 } },
              }} />
              <Typography variant="caption" sx={{ color: '#92400e', fontWeight: 600 }}>
                {tString('teachAI.drawModeHint')}
              </Typography>
            </Box>
          )}

          {/* Loading */}
          {isLoadingPdf && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 2, minHeight: 400 }}>
              <CircularProgress size={40} sx={{ color: ACCENT }} />
              <Typography variant="body2" sx={{ color: TEXT_SECONDARY }}>{tString('teachAI.loadingPdf')}</Typography>
            </Box>
          )}
          {error && <Alert severity="error" sx={{ m: 2, maxWidth: 500 }}>{error}</Alert>}

          {/* PDF + canvas */}
          {pdfBlob && !isLoadingPdf && (
            <Box sx={{ position: 'relative', my: 2 }}>
              <Document
                file={pdfBlob}
                onLoadSuccess={handleDocumentLoadSuccess}
                onLoadError={handleDocumentLoadError}
                loading={<CircularProgress sx={{ color: ACCENT }} />}
                error={<Alert severity="error">{tString('teachAI.failedToLoadPdf')}</Alert>}
              >
                <div ref={pageRef}>
                  <Page
                    pageNumber={currentPage}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    onRenderSuccess={handlePageRenderSuccess}
                  />
                </div>
              </Document>
              {pageDimensions && (
                <canvas
                  ref={canvasRef}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={drawMode ? handleMouseUp : undefined}
                  style={{
                    position: 'absolute', top: 0, left: 0,
                    width: `${pageDimensions.width}px`,
                    height: `${pageDimensions.height}px`,
                    pointerEvents: 'auto',
                    zIndex: 10,
                    cursor: drawMode ? 'crosshair' : (isHoveringBox ? 'pointer' : 'default'),
                  }}
                />
              )}
            </Box>
          )}

          {/* Page navigator */}
          {numPages > 1 && (
            <Box
              sx={{
                position: 'sticky', bottom: 0, zIndex: 50,
                display: 'flex', alignItems: 'center', gap: 2,
                px: 3, py: 1,
                bgcolor: isDarkMode ? 'rgba(42,42,42,0.92)' : 'rgba(234,233,228,0.92)',
                backdropFilter: 'blur(8px)',
                borderTop: `1px solid ${BORDER}`,
                width: '100%', justifyContent: 'center',
              }}
            >
              <IconButton
                size="small"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                sx={{
                  color: currentPage === 1 ? '#adb5bd' : '#374151', fontSize: 20,
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' }
                }}
              >
                ‹
              </IconButton>
              <Typography variant="caption" sx={{ color: isDarkMode ? TEXT_PRIMARY : '#374151', fontWeight: 600, minWidth: 90, textAlign: 'center' }}>
                {tString('teachAI.pageLabel', currentPage, numPages)}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setCurrentPage(prev => Math.min(numPages, prev + 1))}
                disabled={currentPage === numPages}
                sx={{
                  color: currentPage === numPages ? '#adb5bd' : '#374151', fontSize: 20,
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' }
                }}
              >
                ›
              </IconButton>
            </Box>
          )}
        </Box>

        {/* RIGHT: Control panel */}
        <Box
          sx={{
            width: 320, flexShrink: 0,
            bgcolor: PANEL_BG2,
            borderLeft: `1px solid ${BORDER}`,
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Status section */}
          <Box sx={{ p: 2, borderBottom: `1px solid ${BORDER}`, flexShrink: 0 }}>
            {isLoadingMappedFields && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={14} sx={{ color: ACCENT }} />
                <Typography variant="caption" sx={{ color: TEXT_SECONDARY }}>{tString('teachAI.loadingRules')}</Typography>
              </Box>
            )}
            {isExtracting && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={14} sx={{ color: ACCENT }} />
                <Typography variant="caption" sx={{ color: TEXT_SECONDARY }}>{tString('teachAI.extractingText')}</Typography>
              </Box>
            )}

            {/* Idle empty-state hint */}
            {!isLoadingMappedFields && !isExtracting && !showFieldMapper && savedBoxes.length === 0 && (
              <Box
                sx={{
                  p: 2, borderRadius: 2,
                  bgcolor: `${ACCENT}0F`,
                  border: `1px dashed ${ACCENT}4D`,
                  textAlign: 'center',
                }}
              >
                <DrawIcon sx={{ fontSize: 28, color: ACCENT, mb: 0.5, opacity: 0.5 }} />
                <Typography variant="body2" sx={{ color: TEXT_SECONDARY, fontSize: 12, lineHeight: 1.5 }}
                  dangerouslySetInnerHTML={{ __html: tString('teachAI.emptyStateHint') }}
                />
              </Box>
            )}

            {/* Rules summary */}
            {savedBoxes.length > 0 && !isLoadingMappedFields && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: GREEN }} />
                  <Typography variant="caption" sx={{ color: GREEN, fontWeight: 600 }}>
                    {savedBoxes.length !== 1
                      ? tString('teachAI.rulesSavedPlural', savedBoxes.length)
                      : tString('teachAI.rulesSaved', savedBoxes.length)}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: TEXT_MUTED }}>
                  {tString('teachAI.onThisPage', savedBoxes.filter(b => b.pageNumber === currentPage).length)}
                </Typography>
              </Box>
            )}

            {/* Selected-box hint */}
            {selectedBox && (
              <Box
                sx={{
                  mt: 1.5, p: 1.5, borderRadius: 1.5,
                  bgcolor: 'rgba(239,68,68,0.06)',
                  border: '1px solid rgba(239,68,68,0.25)',
                }}
              >
                <Typography variant="caption" sx={{ color: RED_DEL, fontWeight: 700, display: 'block', mb: 0.75 }}>
                  ✦ {tString('teachAI.ruleSelected')}
                </Typography>
                {/* Actionable delete button — selecting a field (by clicking its
                    card or box) used to show only this hint with no control, so
                    a click left the user with nothing to press. The button opens
                    the confirm dialog for both mapped rules and extracted fields
                    (handleDeleteSelectedBox falls back to selectedBox). */}
                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  fullWidth
                  disableElevation
                  startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
                  onClick={() => handleDeleteSelectedBox()}
                  sx={{ textTransform: 'none', fontWeight: 700 }}
                >
                  {tString('common.delete')} {selectedBox.fieldLabel}
                </Button>
              </Box>
            )}
          </Box>

          {/* Field mapper panel */}
          {showFieldMapper && extractedText && (
            <Box
              sx={{
                p: 2, borderBottom: `1px solid ${BORDER}`, flexShrink: 0,
                background: `linear-gradient(180deg, ${ACCENT}0A 0%, transparent 100%)`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: ACCENT }} />
                <Typography variant="overline" sx={{ fontSize: 10, color: TEXT_MUTED, letterSpacing: 1.4 }}>
                  {tString('teachAI.extractedText')}
                </Typography>
              </Box>
              <Box
                sx={{
                  p: 1.5, borderRadius: 1.5, mb: 2,
                  bgcolor: 'rgba(0,0,0,0.03)',
                  border: `1px solid ${BORDER}`,
                  fontFamily: 'monospace', fontSize: 13, color: TEXT_PRIMARY,
                  wordBreak: 'break-word', maxHeight: 80, overflowY: 'auto',
                }}
              >
                {extractedText}
              </Box>

              <Typography variant="overline" sx={{ fontSize: 10, color: TEXT_MUTED, letterSpacing: 1.4, display: 'block', mb: 1 }}>
                {tString('teachAI.mapToField')}
              </Typography>
              <Autocomplete
                value={availableFields.find(field => field.value === selectedField) || null}
                onChange={(event, newValue) => setSelectedField(newValue?.value || '')}
                options={availableFields}
                getOptionLabel={(option) => option.label}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={tString('teachAI.searchFields')}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: isDarkMode ? theme.palette.background.paper : '#ffffff', color: TEXT_PRIMARY, fontSize: 13,
                        '& fieldset': { borderColor: BORDER },
                        '&:hover fieldset': { borderColor: ACCENT },
                        '&.Mui-focused fieldset': { borderColor: ACCENT },
                      },
                      '& .MuiInputBase-input::placeholder': { color: TEXT_MUTED },
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <MenuItem {...props} key={option.value} sx={{ fontSize: 13 }}>
                    {option.label}
                  </MenuItem>
                )}
                filterOptions={(options, state) => {
                  const q = state.inputValue.toLowerCase();
                  return options.filter(o => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q));
                }}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                noOptionsText={tString('teachAI.noFieldsFound')}
                clearOnBlur={false}
                handleHomeEndKeys
                openOnFocus
                blurOnSelect
                slotProps={{ paper: { sx: { maxHeight: 260, overflow: 'auto', bgcolor: isDarkMode ? theme.palette.background.paper : '#ffffff', color: TEXT_PRIMARY } } }}
              />

              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleFieldMapping}
                  disabled={!selectedField || isSavingRule}
                  fullWidth
                  sx={{
                    bgcolor: ACCENT, fontWeight: 700, borderRadius: 1.5,
                    '&:hover': { bgcolor: ACCENT2 },
                    '&.Mui-disabled': { bgcolor: `${ACCENT}26`, color: TEXT_MUTED },
                  }}
                >
                  {isSavingRule ? 'Saving…' : tString('teachAI.applyMapping')}
                </Button>
                <IconButton
                  size="small"
                  onClick={() => { setShowFieldMapper(false); setBoundingBox(null); setExtractedText(''); }}
                  sx={{
                    color: TEXT_MUTED, border: `1px solid ${BORDER}`, borderRadius: 1.5,
                    '&:hover': { color: TEXT_PRIMARY, borderColor: BORDER }
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          )}

          {/* Rules list — each card has its own inline trash button */}
          {savedBoxes.length > 0 && (
            <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
              <Typography variant="overline" sx={{ fontSize: 10, color: TEXT_MUTED, letterSpacing: 1.4, display: 'block', mb: 1 }}>
                {tString('teachAI.savedRules')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {savedBoxes.map((box, idx) => {
                  const isSelected = selectedBox?.fieldPath === box.fieldPath;
                  return (
                    <Box
                      key={`${box.fieldPath}-${idx}`}
                      sx={{
                        p: 1.25, borderRadius: 1.5,
                        bgcolor: isSelected ? `${RED_DEL}0F` : (isDarkMode ? theme.palette.background.paper : '#ffffff'),
                        border: `1px solid ${isSelected ? `${RED_DEL}4D` : BORDER}`,
                        transition: 'all 0.15s',
                        '&:hover': {
                          bgcolor: isSelected ? `${RED_DEL}17` : `${ACCENT}0A`,
                          borderColor: isSelected ? `${RED_DEL}66` : `${ACCENT}40`,
                          '& .trash-btn': { opacity: 1 },
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box
                          onClick={() => setSelectedBox(isSelected ? null : box)}
                          sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: isSelected ? RED_DEL : ACCENT, flexShrink: 0 }} />
                            <Typography variant="caption" sx={{ color: isSelected ? RED_DEL : ACCENT, fontWeight: 700, fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {box.fieldLabel}
                            </Typography>
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: TEXT_MUTED, display: 'block', fontSize: 10.5,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', pl: 1.5
                            }}
                          >
                            {box.extractedText}
                          </Typography>
                        </Box>

                        {/* Page badge */}
                        <Box sx={{
                          fontSize: 10, color: TEXT_MUTED,
                          bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                          borderRadius: 1, px: 0.75, py: 0.25, flexShrink: 0, lineHeight: 1.8,
                        }}>
                          p{box.pageNumber}
                        </Box>

                        {/* Inline delete button */}
                        <IconButton
                          className="trash-btn"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteSelectedBox(box);
                          }}
                          sx={{
                            opacity: isSelected ? 1 : 0,
                            transition: 'opacity 0.15s, color 0.15s',
                            color: isSelected ? RED_DEL : TEXT_MUTED,
                            p: 0.5,
                            '&:hover': { color: RED_DEL, bgcolor: 'rgba(239,68,68,0.08)' },
                          }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Extracted-values list (Ghost Layer) — only CITED values are shown */}
          {showXRay && xrayAvailable && (
            <Box sx={{ flex: 1, overflowY: 'auto', p: 2, borderTop: savedBoxes.length > 0 ? `1px solid ${BORDER}` : 'none' }}>
              <Typography variant="overline" sx={{ fontSize: 10, color: GROUNDING_COLORS.cited, letterSpacing: 1.4, display: 'block', mb: 1 }}>
                Extracted Fields ({xrayBoxes.filter(b => b.pageNumber === currentPage).length} on this page)
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {xrayBoxes.filter(b => b.pageNumber === currentPage).map((box, idx) => {
                  const stColor = GROUNDING_COLORS[box.groundingState] || '#f59e0b';
                  const isSelected = selectedBox?.fieldPath === box.fieldPath;
                  // Reveal the trash on hover OR selection — clicking a card
                  // selects it, and the delete control must follow the selection
                  // (not only hover), so a click isn't a dead end.
                  const active = hoveredFieldPath === box.fieldPath || isSelected;
                  return (
                    <Box
                      key={`xray-${box.fieldPath}-${idx}`}
                      onMouseEnter={() => setHoveredFieldPath(box.fieldPath)}
                      onMouseLeave={() => setHoveredFieldPath(null)}
                      onClick={() => setSelectedBox(isSelected ? null : box)}
                      sx={{
                        p: 1.25, borderRadius: 1.5, cursor: 'pointer',
                        bgcolor: active ? `${stColor}1A` : (isDarkMode ? theme.palette.background.paper : '#ffffff'),
                        border: `1px solid ${active ? stColor : `${stColor}55`}`,
                        transition: 'background-color 0.12s, border-color 0.12s',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: stColor, flexShrink: 0 }} />
                            <Typography variant="caption" sx={{ color: stColor, fontWeight: 700, fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {box.fieldLabel}
                            </Typography>
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: TEXT_MUTED, display: 'block', fontSize: 10.5,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', pl: 1.5,
                            }}
                          >
                            {box.extractedText}
                          </Typography>
                        </Box>
                        <Box sx={{
                          fontSize: 9.5, fontWeight: 700, color: stColor,
                          bgcolor: `${stColor}1A`,
                          borderRadius: 1, px: 0.75, py: 0.25, flexShrink: 0, lineHeight: 1.8,
                          textTransform: 'uppercase', letterSpacing: 0.3,
                        }}>
                          {GROUNDING_LABELS[box.groundingState]}
                        </Box>
                        {/* Delete → confirm dialog → suppress (undo via Restore deleted) */}
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); handleDeleteSelectedBox(box); }}
                          sx={{
                            opacity: active ? 1 : 0,
                            transition: 'opacity 0.15s, color 0.15s',
                            color: active ? RED_DEL : TEXT_MUTED,
                            p: 0.5, flexShrink: 0,
                            '&:hover': { color: RED_DEL, bgcolor: 'rgba(239,68,68,0.08)' },
                          }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Box>
                    </Box>
                  );
                })}
                {xrayBoxes.filter(b => b.pageNumber === currentPage).length === 0 && (
                  <Typography variant="caption" sx={{ color: TEXT_MUTED, fontSize: 10.5 }}>
                    No located fields on this page.
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {/* Footer hint — only shown when no field-mapper is open */}
          {!showFieldMapper && savedBoxes.length > 0 && (
            <Box sx={{ px: 2, py: 1.25, borderTop: `1px solid ${BORDER}`, flexShrink: 0 }}>
              <Typography variant="caption" sx={{ color: TEXT_MUTED, fontSize: 10.5 }}>
                {tString('teachAI.hoverDeleteHint')}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Reset Confirmation Dialog */}
      <Dialog
        open={showResetConfirm}
        onClose={() => setShowResetConfirm(false)}
        maxWidth="sm"
        PaperProps={{ sx: { bgcolor: isDarkMode ? theme.palette.background.paper : '#ffffff', color: TEXT_PRIMARY, border: `1px solid ${ACCENT}33`, borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>{tString('teachAI.resetAllTitle')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: TEXT_SECONDARY, fontSize: 14 }}>
            {tString('teachAI.resetAllMessage')}
          </Typography>
          <Typography sx={{ mt: 1.5, color: RED_DEL, fontWeight: 700, fontSize: 13 }}>
            ⚠ {tString('teachAI.resetAllWarning')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setShowResetConfirm(false)}
            disabled={isResettingRules}
            sx={{ color: TEXT_SECONDARY, '&:hover': { color: TEXT_PRIMARY } }}
          >
            {tString('teachAI.cancel')}
          </Button>
          <Button
            onClick={handleResetRules}
            variant="contained"
            disabled={isResettingRules}
            sx={{ bgcolor: RED_DEL, '&:hover': { bgcolor: '#dc2626' }, fontWeight: 700, borderRadius: 1.5 }}
          >
            {isResettingRules ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : tString('teachAI.yesResetAll')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete / Suppress Confirmation Dialog (Ask 1: confirm both actions) */}
      <Dialog
        open={!!confirmDelete}
        onClose={() => { if (!isDeleting) setConfirmDelete(null); }}
        maxWidth="sm"
        PaperProps={{ sx: { bgcolor: isDarkMode ? theme.palette.background.paper : '#ffffff', color: TEXT_PRIMARY, border: `1px solid ${ACCENT}33`, borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          {confirmDelete?.source === 'extracted' ? 'Delete this field?' : 'Delete this mapping?'}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: TEXT_SECONDARY, fontSize: 14 }}>
            {confirmDelete?.source === 'extracted' ? (
              <>
                <strong style={{ color: TEXT_PRIMARY }}>{confirmDelete?.fieldLabel}</strong> will be
                removed and won't be extracted from this vendor's documents again.
              </>
            ) : (
              <>
                The mapping rule for <strong style={{ color: TEXT_PRIMARY }}>{confirmDelete?.fieldLabel}</strong> will
                be deleted and the field cleared.
              </>
            )}
          </Typography>
          <Typography sx={{ mt: 1.5, color: TEXT_MUTED, fontSize: 12.5 }}>
            You can bring it back later with <strong>Restore deleted</strong>.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setConfirmDelete(null)}
            disabled={isDeleting}
            sx={{ color: TEXT_SECONDARY, '&:hover': { color: TEXT_PRIMARY } }}
          >
            {tString('teachAI.cancel')}
          </Button>
          <Button
            onClick={() => { if (confirmDelete) performDelete(confirmDelete); }}
            variant="contained"
            disabled={isDeleting}
            sx={{ bgcolor: RED_DEL, '&:hover': { bgcolor: '#dc2626' }, fontWeight: 700, borderRadius: 1.5 }}
          >
            {isDeleting ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restore deleted (suppressed) fields — per-field list (Ask 2) */}
      <Dialog
        open={showRestoreList}
        onClose={() => setShowRestoreList(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: isDarkMode ? theme.palette.background.paper : '#ffffff', color: TEXT_PRIMARY, border: `1px solid ${ACCENT}33`, borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>Restore deleted fields</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: TEXT_SECONDARY, fontSize: 13, mb: 1.5 }}>
            Fields you deleted for this vendor. Restoring brings the value back and lets it be extracted again.
          </Typography>
          {suppressedFields.length === 0 ? (
            <Typography sx={{ color: TEXT_MUTED, fontSize: 13, py: 2, textAlign: 'center' }}>
              Nothing to restore.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {suppressedFields.map((f) => (
                <Box
                  key={f.fieldPath}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    p: 1.25, borderRadius: 1.5,
                    border: `1px solid ${BORDER}`,
                    bgcolor: isDarkMode ? 'rgba(255,255,255,0.02)' : '#fafafa',
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" sx={{ color: TEXT_PRIMARY, fontWeight: 700, fontSize: 12, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {labelForPath(f.fieldPath)}
                    </Typography>
                    {f.suppressedValue ? (
                      <Typography variant="caption" sx={{ color: TEXT_MUTED, fontSize: 10.5, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {f.suppressedValue}
                      </Typography>
                    ) : null}
                  </Box>
                  <Button
                    size="small"
                    startIcon={restoringPath === f.fieldPath ? undefined : <UndoIcon sx={{ fontSize: 15 }} />}
                    onClick={() => handleRestoreField(f.fieldPath, f.suppressedValue)}
                    disabled={restoringPath !== null}
                    sx={{
                      flexShrink: 0, fontSize: 12, fontWeight: 700, borderRadius: 1.5,
                      color: ACCENT, border: `1px solid ${ACCENT}55`,
                      '&:hover': { bgcolor: `${ACCENT}14`, borderColor: ACCENT },
                    }}
                  >
                    {restoringPath === f.fieldPath ? <CircularProgress size={15} sx={{ color: ACCENT }} /> : 'Restore'}
                  </Button>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setShowRestoreList(false)}
            sx={{ color: TEXT_SECONDARY, '&:hover': { color: TEXT_PRIMARY } }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default TeachAIModal;
